import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { getAudioPlaybackList, type AudioPlaybackListTrack, type AudioTrackFilters } from '../../api'
import { useToast } from '../../components/ToastProvider'
import { useAudioPlayer, type AudioPlayerControls, type AudioPlayerState, type PlayableTrack } from './useAudioPlayer'
import { AdminAudioPlayer } from './AdminAudioPlayer'

type AudioPlayerContextValue = Pick<AudioPlayerState, 'playingTrack' | 'audioLoading' | 'audioPlaying'>
  & Pick<AudioPlayerControls, 'playTrack' | 'stopPlayback'>
  & {
  markTrackDeleted: (trackId: string) => void
  playLibraryTrack: (track: PlayableTrack, filters: AudioTrackFilters) => Promise<void>
  registerPlayNext: (callback: (() => void) | null) => void
}

type QueueSnapshot = {
  currentIndex: number
  failedIndices: Set<number>
  order: number[]
  orderPosition: number
  tracks: AudioPlaybackListTrack[]
  unavailableIndices: Set<number>
}

type QueueView = {
  position: number
  total: number
}

type AdvanceReason = 'ended' | 'error' | 'manual'

const createSequentialOrder = (length: number): number[] =>
  Array.from({ length }, (_, index) => index)

const shuffle = (indices: number[]): number[] => {
  const result = [...indices]

  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1))
    const current = result[index]!
    result[index] = result[swapIndex]!
    result[swapIndex] = current
  }

  return result
}

const createInitialShuffleOrder = (length: number, currentIndex: number): number[] => [
  currentIndex,
  ...shuffle(createSequentialOrder(length).filter((index) => index !== currentIndex))
]

const createNextShuffleOrder = (length: number, previousIndex: number): number[] => {
  const order = shuffle(createSequentialOrder(length))

  if (order.length > 1 && order[0] === previousIndex) {
    const swapIndex = order.findIndex((index) => index !== previousIndex)
    const first = order[0]!
    order[0] = order[swapIndex]!
    order[swapIndex] = first
  }

  return order
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null)

export const AudioPlayerProvider = ({ children }: { children: ReactNode }) => {
  const { toast } = useToast()
  const advanceRef = useRef<(reason: AdvanceReason) => void>(() => {})
  const player = useAudioPlayer({
    onEnded: () => advanceRef.current('ended'),
    onError: () => advanceRef.current('error')
  })
  const playerRef = useRef(player)
  const playNextRef = useRef<(() => void) | null>(null)
  const queueRef = useRef<QueueSnapshot | null>(null)
  const queueRequestTokenRef = useRef(0)
  const [queueView, setQueueView] = useState<QueueView | null>(null)
  const shuffleEnabledRef = useRef(false)
  const [shuffleEnabled, setShuffleEnabled] = useState(false)

  playerRef.current = player

  const clearQueue = useCallback((): void => {
    queueRef.current = null
    setQueueView(null)
  }, [])

  const syncQueueView = useCallback((queue: QueueSnapshot): void => {
    setQueueView({
      position: queue.orderPosition + 1,
      total: queue.tracks.length
    })
  }, [])

  const stopPlayback = useCallback((): void => {
    queueRequestTokenRef.current += 1
    clearQueue()
    playerRef.current.stopPlayback()
  }, [clearQueue])

  const advanceQueue = useCallback((reason: AdvanceReason): void => {
    const queue = queueRef.current

    if (!queue) {
      return
    }

    if (reason === 'ended') {
      queue.failedIndices.clear()
    } else if (reason === 'error') {
      queue.failedIndices.add(queue.currentIndex)
    }

    const playNextAvailable = (): boolean => {
      for (let position = queue.orderPosition + 1; position < queue.order.length; position += 1) {
        const nextIndex = queue.order[position]!

        if (queue.unavailableIndices.has(nextIndex) || queue.failedIndices.has(nextIndex)) {
          continue
        }

        queue.currentIndex = nextIndex
        queue.orderPosition = position
        syncQueueView(queue)
        playerRef.current.playTrack(queue.tracks[nextIndex]!, { restart: true })
        return true
      }

      return false
    }

    if (playNextAvailable()) {
      return
    }

    queue.order = shuffleEnabledRef.current
      ? createNextShuffleOrder(queue.tracks.length, queue.currentIndex)
      : createSequentialOrder(queue.tracks.length)
    queue.orderPosition = -1

    if (playNextAvailable()) {
      return
    }

    clearQueue()
    playerRef.current.stopPlayback()
    toast('No playable tracks remain in this queue.')
  }, [clearQueue, syncQueueView, toast])

  const toggleShuffle = useCallback((): void => {
    const enabled = !shuffleEnabledRef.current
    const queue = queueRef.current

    shuffleEnabledRef.current = enabled
    setShuffleEnabled(enabled)

    if (!queue) {
      return
    }

    queue.order = enabled
      ? createInitialShuffleOrder(queue.tracks.length, queue.currentIndex)
      : createSequentialOrder(queue.tracks.length)
    queue.orderPosition = enabled ? 0 : queue.currentIndex
    syncQueueView(queue)
  }, [syncQueueView])

  advanceRef.current = advanceQueue

  const registerPlayNext = useCallback((callback: (() => void) | null): void => {
    playNextRef.current = callback
  }, [])

  const handleNext = useCallback((): void => {
    if (queueRef.current) {
      advanceQueue('manual')
      return
    }

    playNextRef.current?.()
  }, [advanceQueue])

  const playTrack = useCallback((track: PlayableTrack): void => {
    if (playerRef.current.playingTrack?.id !== track.id) {
      queueRequestTokenRef.current += 1
      clearQueue()
    }

    playerRef.current.playTrack(track)
  }, [clearQueue])

  const playLibraryTrack = useCallback(async (
    track: PlayableTrack,
    filters: AudioTrackFilters
  ): Promise<void> => {
    if (playerRef.current.playingTrack?.id === track.id) {
      playerRef.current.playTrack(track)
      return
    }

    const requestToken = queueRequestTokenRef.current + 1
    queueRequestTokenRef.current = requestToken
    clearQueue()
    playerRef.current.playTrack(track, { restart: true })

    try {
      const tracks = await getAudioPlaybackList(filters)

      if (queueRequestTokenRef.current !== requestToken) {
        return
      }

      const startIndex = tracks.findIndex((item) => item.id === track.id)

      if (startIndex < 0) {
        throw new Error('The selected track is no longer playable')
      }

      const queue: QueueSnapshot = {
        currentIndex: startIndex,
        failedIndices: new Set<number>(),
        order: shuffleEnabledRef.current
          ? createInitialShuffleOrder(tracks.length, startIndex)
          : createSequentialOrder(tracks.length),
        orderPosition: shuffleEnabledRef.current ? 0 : startIndex,
        tracks,
        unavailableIndices: new Set<number>()
      }

      queueRef.current = queue
      syncQueueView(queue)
    } catch {
      if (queueRequestTokenRef.current !== requestToken) {
        return
      }

      clearQueue()
      toast('The track is playing, but the playback queue could not be loaded.')
    }
  }, [clearQueue, syncQueueView, toast])

  const markTrackDeleted = useCallback((trackId: string): void => {
    const queue = queueRef.current

    if (!queue) {
      if (playerRef.current.playingTrack?.id === trackId) {
        playerRef.current.stopPlayback()
      }
      return
    }

    const deletedIndex = queue.tracks.findIndex((track) => track.id === trackId)

    if (deletedIndex < 0) {
      return
    }

    queue.unavailableIndices.add(deletedIndex)

    if (queue.currentIndex === deletedIndex) {
      advanceQueue('manual')
    }
  }, [advanceQueue])

  useEffect(() => {
    return () => {
      queueRequestTokenRef.current += 1
      playNextRef.current = null
      queueRef.current = null
    }
  }, [])

  const contextValue = useMemo<AudioPlayerContextValue>(() => ({
    playingTrack: player.playingTrack,
    audioLoading: player.audioLoading,
    audioPlaying: player.audioPlaying,
    markTrackDeleted,
    playLibraryTrack,
    playTrack,
    stopPlayback,
    registerPlayNext
  }), [
    markTrackDeleted,
    playLibraryTrack,
    player.audioLoading,
    player.audioPlaying,
    player.playingTrack,
    playTrack,
    registerPlayNext,
    stopPlayback
  ])

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
      <AdminAudioPlayer
        activeAudioSource={player.activeAudioSource}
        currentTime={player.audioCurrentTime}
        duration={player.audioDuration}
        isPlaying={player.audioPlaying}
        loading={player.audioLoading}
        muted={player.audioMuted}
        preferLossless={player.preferLossless}
        queuePosition={queueView?.position}
        queueTotal={queueView?.total}
        shuffleEnabled={shuffleEnabled}
        showLosslessToggle
        track={player.playingTrack}
        volume={player.audioVolume}
        onSeek={player.seekPlayback}
        onSetLosslessPreference={player.setLosslessPreference}
        onToggleShuffle={queueView ? toggleShuffle : undefined}
        onTogglePlayback={player.togglePlayback}
        onToggleMute={player.toggleAudioMute}
        onVolumeChange={player.updateAudioVolume}
        onStop={stopPlayback}
        onNext={handleNext}
      />
    </AudioPlayerContext.Provider>
  )
}

export const useAudioPlayerContext = (): AudioPlayerContextValue => {
  const ctx = useContext(AudioPlayerContext)
  if (!ctx) throw new Error('useAudioPlayerContext must be used within AudioPlayerProvider')
  return ctx
}
